import * as React from 'react'
import { Play, Pause, Gauge, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { RECORDING_STAGE_TIMEOUT_MS } from '@/config/constants'
import { RecordingLoadError } from '@/features/calls/components/RecordingLoadError'
import { useRecordingPlayback } from '@/features/calls/useRecordingPlayback'
import { cn } from '@/lib/utils'
import { withTimeout } from '@/lib/withTimeout'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface AudioPlayerProps {
  /**
   * Signed playback URL, when the caller already has one. Note there is no
   * "raw stored URL" prop: the calls list serves unsigned R2 URLs that have not
   * been playable since VAPI locked down public access, so the only URL this
   * player will ever load is one that came back signed.
   */
  resolvedRecordingUrl?: string | null
  resolveRecordingUrl?: (options?: { force?: boolean }) => Promise<string>
  onResolvedRecordingUrlError?: () => void
  isLoading?: boolean
  /**
   * Imperative click-to-seek hook-up, same contract as
   * `StereoWaveformPlayer.seekRef`: the player writes a `(timeSeconds) =>
   * void` into the ref; consumers (e.g. transcript turns) call it to jump
   * playback to a timestamp. Resolves the signed recording URL first when
   * playback hasn't started yet.
   */
  seekRef?: React.MutableRefObject<((timeSeconds: number) => void) | null>
}

export function AudioPlayer({
  resolvedRecordingUrl,
  resolveRecordingUrl,
  onResolvedRecordingUrlError,
  isLoading = false,
  seekRef,
}: AudioPlayerProps) {
  const audioRef = React.useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = React.useState(false)
  const [currentTime, setCurrentTime] = React.useState(0)
  const [duration, setDuration] = React.useState(0)
  const [playbackRate, setPlaybackRate] = React.useState(1)
  // Where to pick playback back up once a replacement URL has loaded.
  const pendingRestoreRef = React.useRef<{
    time: number
    rate: number
    resume: boolean
  } | null>(null)
  // Play *intent*, tracked separately from `isPlaying` because `isPlaying` only
  // turns true after play() resolves — too late for an error that fires during
  // that same call. Mirrors wantsPlaybackRef in StereoWaveformPlayer.
  const wantsPlaybackRef = React.useRef(false)
  // Read at event time only. Keeping them out of the listener effect's deps is
  // what stops it tearing down and re-attaching every listener on each
  // `timeupdate` tick.
  const latestRef = React.useRef({ currentTime, isPlaying, playbackRate })
  latestRef.current = { currentTime, isPlaying, playbackRate }

  const {
    url: playbackUrl,
    isResolving,
    error,
    ensure,
    heal,
    getIsHealing,
    notePlaybackProgress,
    retry,
  } = useRecordingPlayback({
    resolvedRecordingUrl,
    resolveRecordingUrl,
    isRecordingUrlLoading: isLoading,
    onResolvedRecordingUrlError,
    // A replacement identical to the URL that just died means React writes no
    // src attribute, so nothing reloads, `canplay` never fires, the pending
    // restore is never consumed, and the error surface has already cleared —
    // an enabled play button on a dead element. Safe to reset position here:
    // the pending restore puts it back.
    onUrlSettled: (_url, { unchanged }) => {
      if (unchanged) audioRef.current?.load()
    },
  })

  React.useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const updateTime = () => {
      setCurrentTime(audio.currentTime)
      // Re-arms automatic recovery, but only once *playback* has actually got
      // past the point it died at — see notePlaybackProgress. `timeupdate`
      // also fires for seeks, and the scrubber assigns currentTime on every
      // input event, so without this a single drag across a dead recording
      // would re-arm repeatedly and bill one resolve per media error.
      if (!audio.paused && !audio.seeking)
        notePlaybackProgress(audio.currentTime)
    }
    const updateDuration = () => setDuration(audio.duration)
    const handleEnded = () => {
      wantsPlaybackRef.current = false
      setIsPlaying(false)
    }

    // Pick up where the replaced URL left off.
    const handleCanPlay = () => {
      const pending = pendingRestoreRef.current
      if (!pending) return
      pendingRestoreRef.current = null
      audio.playbackRate = pending.rate
      if (pending.time > 0) audio.currentTime = pending.time
      if (!pending.resume) return
      void audio
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false))
    }

    // The only signal we trust that a signed URL has gone bad. Recover without
    // making the user click again — needing that second click is what turned an
    // expired URL into a spinner nobody could get out of.
    const handleError = () => {
      // A real element can fire error twice — the failing load, then its
      // replacement. Without this guard the second one overwrites the saved
      // position with the already-reset zero.
      if (getIsHealing()) return
      const latest = latestRef.current
      pendingRestoreRef.current = {
        time: latest.currentTime,
        rate: latest.playbackRate,
        // Intent, not state: on a first click against an expired URL play()
        // rejects before isPlaying ever turns true, so reading isPlaying here
        // would skip the resume and demand a second click.
        resume: wantsPlaybackRef.current,
      }
      setIsPlaying(false)
      // Deliberately does not clear pendingRestoreRef when the heal fails:
      // Retry heals from the same offset, and clearing it made handleCanPlay
      // bail on its first line, so Retry landed a working URL but rewound to
      // 0:00 at 1x and paused. It is consumed once, by whichever load succeeds.
      // An identical replacement URL is handled by onUrlSettled, which fires
      // for Retry too — this path never sees that one.
      void heal(latest.currentTime)
    }

    audio.addEventListener('timeupdate', updateTime)
    audio.addEventListener('loadedmetadata', updateDuration)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('canplay', handleCanPlay)
    audio.addEventListener('error', handleError)

    return () => {
      audio.removeEventListener('timeupdate', updateTime)
      audio.removeEventListener('loadedmetadata', updateDuration)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('canplay', handleCanPlay)
      audio.removeEventListener('error', handleError)
    }
  }, [getIsHealing, heal, notePlaybackProgress])

  // Keyed on the URL we actually play, not on a prop that never changed — the
  // old version left a stale duration on screen next to a spinner.
  React.useEffect(() => {
    setCurrentTime(0)
    setDuration(0)
    setIsPlaying(false)
  }, [playbackUrl])

  React.useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.playbackRate = playbackRate
  }, [playbackRate])

  // Latest seek target requested while playback isn't ready yet (signed-URL
  // resolve in flight, or the recording still loading) — a later transcript
  // click overwrites it instead of being dropped, and `startPlayback` consumes
  // it once a URL has landed, regardless of whether Play or a transcript click
  // kicked off the resolve.
  const pendingSeekRef = React.useRef<number | null>(null)

  /**
   * Consume the pending seek target, if any: point playback at it and clear
   * the ref. Before metadata arrives the currentTime assignment sets the
   * default playback start position, after it's a real seek — both land on
   * the target.
   */
  const applyPendingSeek = (audio: HTMLAudioElement) => {
    const target = pendingSeekRef.current
    pendingSeekRef.current = null
    if (target === null || !Number.isFinite(target)) return
    audio.currentTime = target
    setCurrentTime(target)
  }

  /**
   * The single route into playback, shared by Play and click-to-seek: make sure
   * a signed URL exists, attach it, honour any pending transcript-click target,
   * then play. Both entry points go through here so neither can reach the media
   * element bypassing URL recovery.
   */
  const startPlayback = async () => {
    const audio = audioRef.current
    if (!audio) return

    // Recorded before the awaits below: an expired URL rejects play() while
    // isPlaying is still false, and the error handler needs to know the user
    // asked for playback.
    wantsPlaybackRef.current = true
    const nextUrl = await ensure()
    if (!nextUrl) {
      pendingSeekRef.current = null
      // Intent must not outlive a start that never happened, or a later `error`
      // would heal *and* resume — a recording playing out loud unprompted.
      wantsPlaybackRef.current = false
      setIsPlaying(false)
      return
    }

    // Only reassign when it actually changed; re-setting src reloads the media
    // and throws away the position. getAttribute avoids comparing our string
    // against the browser's absolutised src.
    if (audio.getAttribute('src') !== nextUrl) {
      audio.src = nextUrl
    }

    // Before play(), so playback starts at the clicked turn rather than seeking
    // audibly after it has already begun.
    applyPendingSeek(audio)

    try {
      // play() fetches whatever it needs on its own and rejects if the media
      // can't load, so there's nothing to wait on first. Calling load() here
      // instead would re-request the file and reset currentTime to 0 on every
      // click, throwing away the position the guard above just protected.
      await withTimeout(
        audio.play(),
        RECORDING_STAGE_TIMEOUT_MS,
        'Timed out loading the recording'
      )
      setIsPlaying(true)
    } catch {
      // Recovery is driven by the element's `error` event, which fires for a
      // failed load. A bare timeout leaves the button clickable again.
      // withTimeout abandons rather than cancels, so a slow-but-successful load
      // can still start audio after we gave up — pause so the element and the
      // Play icon agree about what the timeout meant.
      audio.pause()
      // Same reason as the !nextUrl branch: the user has seen the Play icon come
      // back and moved on, so a later `error` must not start playback on its
      // own. Safe for the codex resume case — a failed *load* fires `error`
      // before this promise rejects, so handleError has already captured
      // resume: true. The paths that reach here without an `error` event at all
      // (the withTimeout rejection, NotAllowedError) are exactly the ones where
      // intent should not survive.
      wantsPlaybackRef.current = false
      setIsPlaying(false)
    }
  }

  const togglePlayPause = async () => {
    const audio = audioRef.current
    if (!audio || isResolving) return

    if (isPlaying) {
      wantsPlaybackRef.current = false
      audio.pause()
      setIsPlaying(false)
      return
    }

    await startPlayback()
  }

  /**
   * Jump playback to `timeSeconds` and play. Routed through `startPlayback` for
   * the same reason Play is: a transcript click must not be able to reach the
   * media element without going past URL recovery first.
   *
   * A click that lands while a resolve is already in flight only records the
   * pending target — whichever resolve is running consumes it, so the click
   * still lands rather than being dropped or needing a second one.
   */
  const seekTo = async (timeSeconds: number) => {
    // secondsFromStart is typed as number but can be missing in real Vapi
    // payloads — never point currentTime at undefined/NaN.
    if (!Number.isFinite(timeSeconds)) return
    pendingSeekRef.current = timeSeconds
    if (isResolving) return

    await startPlayback()
  }

  // Publish the seek handle for consumers (CallLogsRecording's transcript
  // turns). useImperativeHandle (re-run every render) keeps the closure
  // fresh and nulls the ref on unmount.
  const localSeekRef = React.useRef<((timeSeconds: number) => void) | null>(
    null
  )
  React.useImperativeHandle(seekRef ?? localSeekRef, () => seekTo)

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current
    if (!audio) return
    const newTime = parseFloat(e.target.value)
    audio.currentTime = newTime
    setCurrentTime(newTime)
  }

  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const progressPct =
    duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0

  return (
    <>
      {/* Rendered unconditionally, even behind the error surface. Unmounting it
          would remount a fresh element on Retry, and the listener effect above
          depends only on stable callbacks so it would never re-attach — leaving
          a player with no timeupdate, no duration, and no auto-recovery. */}
      <audio ref={audioRef} src={playbackUrl ?? undefined} preload="metadata" />

      {error ? (
        <RecordingLoadError message={error} onRetry={() => void retry()} />
      ) : (
        /* No outer border/bg/padding here — the consumer (CallLogsRecording)
           owns the card chrome and decides spacing. Player fills its parent
           exactly so consumers don't see nested boxes or dead padding. */
        <div className="flex items-center gap-3">
          {/* Play / Pause */}
          <Button
            variant="ghost"
            size="icon"
            disabled={isResolving}
            onClick={togglePlayPause}
            aria-label={
              isResolving ? 'Loading recording' : isPlaying ? 'Pause' : 'Play'
            }
            className="text-primary-high-contrast h-8 w-8 shrink-0 hover:bg-transparent">
            {isResolving ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : isPlaying ? (
              <Pause className="h-5 w-5" strokeWidth={2} fill="currentColor" />
            ) : (
              <Play className="h-5 w-5" strokeWidth={1.75} />
            )}
          </Button>

          {/* Current time */}
          <span className="text-primary-high-contrast min-w-[5ch] text-sm tabular-nums">
            {formatTime(currentTime)}
          </span>

          {/* Scrubber */}
          <input
            type="range"
            min="0"
            max={duration || 0}
            step="0.01"
            value={currentTime}
            onChange={handleProgressChange}
            aria-label="Seek"
            className={cn(
              'h-1.5 flex-1 cursor-pointer appearance-none rounded-full',
              // Track gradient: primary-solid fill behind the thumb, neutral track behind it.
              // The thumb is invisible by default and reveals on hover/focus/drag.
              '[&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:rounded-full',
              '[&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:-mt-1 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary-solid [&::-webkit-slider-thumb]:opacity-0 [&::-webkit-slider-thumb]:transition-opacity',
              'hover:[&::-webkit-slider-thumb]:opacity-100 focus:[&::-webkit-slider-thumb]:opacity-100',
              '[&::-moz-range-thumb]:h-3.5 [&::-moz-range-thumb]:w-3.5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary-solid [&::-moz-range-thumb]:opacity-0 [&::-moz-range-thumb]:transition-opacity',
              'hover:[&::-moz-range-thumb]:opacity-100 focus:[&::-moz-range-thumb]:opacity-100'
            )}
            style={{
              background: `linear-gradient(to right, var(--primary-solid) 0%, var(--primary-solid) ${progressPct}%, var(--neutral-ui-bg) ${progressPct}%, var(--neutral-ui-bg) 100%)`,
            }}
          />

          {/* Duration */}
          <span className="text-neutral-low-contrast min-w-[5ch] text-sm tabular-nums">
            {duration > 0 ? formatTime(duration) : '--:--'}
          </span>

          {/* Speed pill */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                aria-label="Playback speed"
                className="bg-primary-ui-bg text-primary-solid hover:bg-primary-hover-bg h-8 gap-1 rounded-md px-2 text-sm font-medium shrink-0">
                <Gauge className="h-4 w-4" />
                {playbackRate}x
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[7rem]">
              {[0.5, 0.75, 1, 1.25, 2].map((rate) => {
                const isActive = playbackRate === rate
                return (
                  <DropdownMenuItem
                    key={rate}
                    onClick={() => setPlaybackRate(rate)}
                    className="gap-2">
                    <span
                      aria-hidden
                      className={cn(
                        'h-1.5 w-1.5 rounded-full',
                        isActive ? 'bg-primary-solid' : 'bg-transparent'
                      )}
                    />
                    {rate}x
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </>
  )
}
